import { useState } from 'react'
import { Link } from 'react-router'

import { QuantLiveRow } from '@/app/components/QuantLiveRow'
import { useNowTick } from '@/app/hooks/useNowTick'
import { refreshLiveSessions, useLivePaneOpen, type QuantLiveListState } from '@/app/hooks/useQuantLiveSessions'
import { counterModel, isActive, splitSessions } from '@/app/lib/quant/liveView'
import type { Strategy } from '@/app/lib/quant/types'
import { Button } from '@/ui/Button'
import { QuantUnavailable } from '@/ui/QuantUnavailable'

interface QuantLivePaneProps {
  strategy: Strategy
  state: QuantLiveListState & { retry: () => void }
}

const ENDED_PAGE = 20
const HELD_BY_OTHER = 'Held by another user. Starts are refused.'

const HEADING = 'm-0 font-sans text-[10px] font-bold tracking-[0.2em] text-paper-50 uppercase'
const EMPTY_LINE = 'border-t border-paper-14 py-3 font-mono text-[12px] text-paper-40'

/** Why a strategy cannot start a session, or `null` when it can (spec 041 P3). */
function unsupportedReason(strategy: Strategy): string | null {
  if (strategy.live_error) return `Live support is broken for this strategy: ${strategy.live_error}`
  if (strategy.live === null || strategy.live === undefined) return 'This strategy does not support live sessions.'
  return null
}

/**
 * The strategy's Live tab (spec 041 P3): the counter, a Running and an Ended
 * section of fixed 64 px rows, and the empty, loading, unavailable and stale
 * states. It owns the 1 s tick (cleared on unmount) and tells the sessions store
 * the pane is open, which turns the 10 s cadence on.
 */
export function QuantLivePane({ strategy, state }: QuantLivePaneProps) {
  useLivePaneOpen(true)
  const now = useNowTick(true)
  const [shown, setShown] = useState(ENDED_PAGE)
  const loaded = state.status === 'loaded'
  const { running, ended } = loaded ? splitSessions(state.sessions, strategy.slug) : { running: [], ended: [] }
  const counter = loaded
    ? counterModel(
        { active: state.active, max: state.max, own_active: state.ownActive },
        state.sessions,
        strategy.slug,
        state.sessions.filter(isActive).length,
      )
    : null
  const none = loaded && running.length === 0 && ended.length === 0
  const reason = unsupportedReason(strategy)

  return (
    <div role="group" aria-label="Live sessions" data-pane="live" className="absolute inset-0 overflow-y-auto">
      <div className="flex max-w-[980px] flex-col gap-4 px-7 pt-5 pb-10">
        <div className="flex min-h-[20px] flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="m-0 font-sans text-[12px] font-bold tracking-[0.18em] uppercase">Live sessions (paper)</h3>
          {counter !== null && (
            <>
              <span data-live-counter className="font-mono text-[12px] text-paper-90">
                {counter.text}
              </span>
              {counter.others.map((other) => (
                <Link
                  key={other.slug}
                  to={`/strategies/${other.slug}/live`}
                  className="font-mono text-[12px] text-accent-line underline-offset-2 hover:underline"
                >
                  {`${other.slug} ${other.count}`}
                </Link>
              ))}
              {counter.heldByOtherText !== null && (
                <span className="font-mono text-[12px] text-paper-50">{counter.heldByOtherText}</span>
              )}
              {counter.capFullHeld && <span className="font-mono text-[12px] text-paper-50">{HELD_BY_OTHER}</span>}
            </>
          )}
        </div>

        {state.status === 'error' && <QuantUnavailable message={state.message} onRetry={state.retry} />}
        {loaded && state.error !== undefined && (
          <QuantUnavailable message={`Live sessions unavailable. ${state.error}`} onRetry={() => refreshLiveSessions()} />
        )}

        {state.status === 'loading' && (
          <>
            <h4 className={HEADING}>Running</h4>
            <div data-live-loading className="h-[64px] border border-paper-14" />
            <h4 className={HEADING}>Ended</h4>
            <div data-live-loading className="h-[64px] border border-paper-14" />
          </>
        )}

        {none && (
          <div data-live-empty className="flex flex-col gap-2 border border-paper-14 px-4 py-5 font-mono text-[12px]">
            {reason === null ? (
              <>
                <span className="text-paper-90">No live sessions for this strategy.</span>
                <Link to={`/strategies/${strategy.slug}/config`} className="text-accent-line underline-offset-2 hover:underline">
                  Start one from Config
                </Link>
              </>
            ) : (
              <span className="text-paper-90">{reason}</span>
            )}
          </div>
        )}

        {loaded && !none && (
          <>
            <h4 className={HEADING}>Running</h4>
            <div data-live-section="running" className="flex flex-col gap-2">
              {running.length === 0 ? (
                <div data-section-empty className={EMPTY_LINE}>
                  None running.
                </div>
              ) : (
                running.map((s) => <QuantLiveRow key={s.id} session={s} now={now} />)
              )}
            </div>
            <h4 className={HEADING}>Ended</h4>
            <div data-live-section="ended" className="flex flex-col gap-2">
              {ended.length === 0 ? (
                <div data-section-empty className={EMPTY_LINE}>
                  None yet.
                </div>
              ) : (
                ended.slice(0, shown).map((s) => <QuantLiveRow key={s.id} session={s} now={now} />)
              )}
              {ended.length > shown && (
                <div>
                  <Button variant="secondary" size="sm" onClick={() => setShown((n) => n + ENDED_PAGE)}>
                    Show 20 more
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
